/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getRelatedProductsAsync, getRelationTypesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductRelationType, ProductSearchResult } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import getFullProducts, { FullProductInput, ProductDetailsCriteria } from './get-full-products';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
import { getProductDetailsCriteriaFromActionInput } from './utilities/utils';

/**
 * GetRelatedProducts Input Action.
 */
export class GetRelatedProductsInput implements IActionInput {
    public readonly productId: number;

    public readonly catalogId: number;

    public readonly relationType: string;

    public ProductDetailsCriteria: ProductDetailsCriteria;

    public readonly queryResultSettingsProxy: QueryResultSettingsProxy;

    public constructor(
        productId: number,
        catalogId: number,
        relationType: string,
        queryResultSettingsProxy: QueryResultSettingsProxy,
        criteria: ProductDetailsCriteria
    ) {
        this.productId = productId;
        this.catalogId = catalogId;
        this.relationType = relationType;
        this.queryResultSettingsProxy = queryResultSettingsProxy;
        this.ProductDetailsCriteria = criteria;
    }

    public getCacheKey = () =>
        `${this.productId}|${this.catalogId}|${this.relationType.toLowerCase()}|${this.queryResultSettingsProxy.cacheKeyHint}|${
            this.ProductDetailsCriteria.getPrice
        }`;

    public getCacheObjectType = () => 'GetRelatedProducts';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * Creates the input required to make GetRelatedProducts retail api call.
 * @param inputData
 */
export const createGetRelatedProductsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext && inputData.config) {
        const catalogId = getCatalogId(inputData.requestContext);
        const relationType = inputData.config.relationType;

        let productId = inputData.requestContext.urlTokens ? Number(inputData.requestContext.urlTokens.recordId) : 0;
        const productDetailsCriteria = getProductDetailsCriteriaFromActionInput(inputData);
        if (!relationType) {
            throw new Error('Input relation type is invalid.');
        }

        // Query string may override the product id from url
        if (inputData.requestContext.query && inputData.requestContext.query.productId) {
            productId = Number(inputData.requestContext.query.productId);
        }

        if (Number.isNaN(productId) || productId <= 0) {
            throw new Error('No valid product id available in url or query string.');
        }

        const queryResultSettingsProxy = QueryResultSettingsProxy.fromInputData(inputData);
        return new GetRelatedProductsInput(productId, catalogId, relationType, queryResultSettingsProxy, productDetailsCriteria);
    }

    throw new Error('Invalid input data or request context');
};

export function searchProductRelationType(productRelationTypes: ProductRelationType[], _productRelationType: string): number | undefined {
    let foundProductRelationTypeId;
    _productRelationType = _productRelationType.toLowerCase();
    productRelationTypes.forEach((productRelationType: ProductRelationType) => {
        if (productRelationType.Name && productRelationType.Name.toLowerCase() === _productRelationType) {
            foundProductRelationTypeId = productRelationType.RecordId;
        }
    });

    return foundProductRelationTypeId;
}

async function getRelatedProductsAction(input: GetRelatedProductsInput, ctx: IActionContext): Promise<FullProduct[]> {
    const apiSettings = ctx.requestContext.apiSettings;
    const querySettings = input.queryResultSettingsProxy.QueryResultSettings;

    const productRelationTypes = await getRelationTypesAsync(
        { callerContext: ctx, queryResultSettings: querySettings },
        input.productId,
        +apiSettings.channelId,
        input.catalogId
    );
    if (!productRelationTypes) {
        ctx.trace(`[getProductRelationType] Unable to get product relation types for product ${input.productId}`);
        return <FullProduct[]>[];
    }
    const productRelationTypeId = searchProductRelationType(productRelationTypes, input.relationType);
    if (!productRelationTypeId) {
        ctx.trace(`[getRelatedProducts] Unable to find relation type ${input.relationType} for product ${input.productId}`);
        return <FullProduct[]>[];
    }

    const productInputs = await getRelatedProductsAsync(
        { callerContext: ctx, queryResultSettings: querySettings },
        input.productId,
        +apiSettings.channelId,
        input.catalogId,
        productRelationTypeId
    )
        .then(response => {
            if (response && response.length > 0) {
                return response.map(
                    (product: ProductSearchResult): FullProductInput => {
                        return new FullProductInput(product.RecordId, apiSettings, input.ProductDetailsCriteria, ctx.requestContext);
                    }
                );
            }

            ctx.trace('[getRelatedProductsAction] Invalid response from server');
            return <FullProductInput[]>[];
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.trace(error.stack || '');
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getRelatedProductsAction] Unable to Fetch Products.');
            ctx.trace('[getRelatedProductsAction] Unable to Fetch Products.');
            return <FullProductInput[]>[];
        });

    if (productInputs.length > 0) {
        return getFullProducts(productInputs, ctx);
    }
    return <FullProduct[]>[];
}

/**
 * The getRelatedProducts data action
 * Uses a productId URL Token and finds the relation types for said product
 * using the getRelationTypes RetailServer API, and then finds and retusn all products that
 * share that relation type via the getRelatedProducts RetailServer API.
 */
export const getRelatedProductsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-related-products',
    action: <IAction<FullProduct[]>>getRelatedProductsAction,
    input: createGetRelatedProductsInput
});

export default getRelatedProductsActionDataAction;
